/**
 * Road roughness, measured from the phone's accelerometer.
 *
 * A rough road costs a delivery real money - it shakes the load, it slows the
 * truck, and it is the difference between a route that looks fine on paper and
 * one nobody wants to drive twice. Nothing in any map tells you this, but the
 * phone is already bolted to the dashboard with an accelerometer in it, so we
 * can simply measure it and remember.
 *
 * How the number is produced
 * --------------------------
 * `devicemotion` gives acceleration including gravity, at whatever rate the
 * device chooses. Gravity is removed with a low-pass estimate and subtracted,
 * leaving the vibration. We take the MAGNITUDE of that residual rather than a
 * single axis, because a phone in a cradle sits at an arbitrary angle and we
 * cannot assume which axis points up. The RMS over a couple of seconds is the
 * raw signal.
 *
 * Two honesty caveats, both handled:
 *  - Vibration grows with speed on any surface, so the figure is normalised
 *    against a 60 km/h reference; without that, a motorway would always look
 *    rougher than a village lane.
 *  - Below ~20 km/h engine idle and door slams dominate, so nothing is
 *    recorded until the vehicle is genuinely moving.
 *
 * The result is a RELATIVE index, not a road-authority measurement: a phone
 * loose in a cup holder reads higher than one in a rigid mount. It is
 * comparable across drives in the same setup, which is what matters for
 * "is this road worse than that one".
 */

/** Reference speed the index is normalised to, km/h. */
const REFERENCE_KMH = 60;
/**
 * Below this the vehicle is not really driving; ignore the signal.
 *
 * This is not only about signal quality. A phone in a cradle at a red light
 * picks up the engine, the driver's door, the aircon and passing lorries, and
 * every one of those was being folded into "how rough is this road". Standing
 * still is not a road-surface measurement at all.
 */
const MIN_KMH = 20;
/** Speed reading older than this is stale - treat as stopped. */
const SPEED_TTL_MS = 4000;
/** Rolling window for the RMS, milliseconds. */
const WINDOW_MS = 2500;
/** Scales raw m/s² RMS into a friendly 0-100 index. */
const INDEX_SCALE = 26;

interface Sample {
  t: number;
  v: number;
}

export class RoughnessMeter {
  private samples: Sample[] = [];
  private gravity: [number, number, number] = [0, 0, 9.81];
  private running = false;
  private handler: ((e: DeviceMotionEvent) => void) | null = null;
  private lastSpeedKmh = 0;
  private speedAt = 0;

  /** Is the platform able to report motion at all? */
  static supported(): boolean {
    return typeof DeviceMotionEvent !== 'undefined';
  }

  /**
   * iOS gates motion behind an explicit grant that must come from a user
   * gesture. Call this from the same tap that starts navigation.
   */
  static async requestPermission(): Promise<boolean> {
    if (!RoughnessMeter.supported()) return false;
    const anyEvent = DeviceMotionEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };
    if (typeof anyEvent.requestPermission !== 'function') return true; // Android: no gate
    try {
      return (await anyEvent.requestPermission()) === 'granted';
    } catch {
      return false;
    }
  }

  start(): void {
    if (this.running || !RoughnessMeter.supported()) return;
    this.running = true;
    this.handler = (e: DeviceMotionEvent) => this.onMotion(e);
    window.addEventListener('devicemotion', this.handler);
  }

  stop(): void {
    if (this.handler) window.removeEventListener('devicemotion', this.handler);
    this.handler = null;
    this.running = false;
    this.samples = [];
  }

  /** Feed the current speed so the index can be normalised. */
  setSpeed(kmh: number): void {
    this.lastSpeedKmh = kmh;
    this.speedAt = Date.now();
  }

  /** Is the vehicle moving fast enough for the reading to mean anything? */
  get measuring(): boolean {
    if (!this.running) return false;
    if (Date.now() - this.speedAt > SPEED_TTL_MS) return false;
    return this.lastSpeedKmh >= MIN_KMH;
  }

  private onMotion(e: DeviceMotionEvent): void {
    const a = e.accelerationIncludingGravity;
    if (!a || a.x == null || a.y == null || a.z == null) return;

    // Low-pass to track gravity, then subtract it to leave vibration only.
    const alpha = 0.9;
    this.gravity = [
      this.gravity[0] * alpha + a.x * (1 - alpha),
      this.gravity[1] * alpha + a.y * (1 - alpha),
      this.gravity[2] * alpha + a.z * (1 - alpha),
    ];
    const dx = a.x - this.gravity[0];
    const dy = a.y - this.gravity[1];
    const dz = a.z - this.gravity[2];
    // Magnitude, so the reading does not depend on how the phone is angled.
    const magnitude = Math.sqrt(dx * dx + dy * dy + dz * dz);

    const now = Date.now();
    this.samples.push({ t: now, v: magnitude });
    const cutoff = now - WINDOW_MS;
    while (this.samples.length && this.samples[0].t < cutoff) this.samples.shift();
  }

  /**
   * Current roughness index, 0 (glass) to 100 (punishing), or null when the
   * vehicle is too slow or there is no usable signal yet.
   */
  read(): number | null {
    if (!this.running || this.samples.length < 8) return null;
    // Stopped, or the speed we were given has gone stale (GPS dropout, app
    // backgrounded). Either way this is not a measurement of the road.
    if (!this.measuring) return null;

    let sum = 0;
    for (const s of this.samples) sum += s.v * s.v;
    const rms = Math.sqrt(sum / this.samples.length);

    // Vibration rises with speed on any surface; normalise so roads compare
    // fairly rather than fast roads always looking worse.
    const speedFactor = Math.sqrt(Math.max(MIN_KMH, this.lastSpeedKmh) / REFERENCE_KMH);
    const normalised = rms / speedFactor;

    return Math.max(0, Math.min(100, Math.round(normalised * INDEX_SCALE)));
  }
}

/** Plain-language band for an index, for display. */
export function roughnessBand(index: number): 'smooth' | 'fair' | 'rough' | 'bad' {
  if (index < 18) return 'smooth';
  if (index < 35) return 'fair';
  if (index < 55) return 'rough';
  return 'bad';
}
